import type { ChartAnnotation, DailyChartInput } from "./input.ts"

/**
 * A mark over the plot: the day it stands on, what it says, and the annotations it stands for.
 * Every annotated day keeps its own dashed line; a mark may stand for several days' annotations.
 */
export type AnnotationMark<A extends ChartAnnotation = ChartAnnotation> = {
  day: string
  label: string
  annotations: A[]
}

/** How wide a mark is, in px: 6px a character of its label, less spaces, and 10px a side. */
export const markWidthOf = (label: string) => label.replaceAll(" ", "").length * 6 + 20

// The least room between two marks before they are drawn as one.
const markGap = 4

/**
 * The chart's marks: one on each annotated day, naming that day's annotations ("1, 2"). Given the
 * plot's width, as on a phone, marks that would overlap there are drawn as one, on the first of
 * their days, and a mark there names its annotations as a run ("3–6") once there are more than two.
 * Annotations are taken in the order given; a day's marks keep that order.
 */
export function annotationMarksOf<K extends string, A extends ChartAnnotation>(
  input: Omit<DailyChartInput<K>, "annotations"> & { annotations?: readonly A[] },
): AnnotationMark<A>[] {
  const { days, annotations = [], plotWidth } = input
  const say = (of: readonly A[]) => {
    const labels = of.map(({ label }) => label)
    return plotWidth !== undefined && labels.length > 2
      ? `${labels[0]}–${labels.at(-1)}`
      : labels.join(", ")
  }
  const band = (plotWidth ?? 0) / Math.max(days.length, 1)
  const centreOf = (index: number) => (index + 0.5) * band
  type Run = { index: number; annotations: A[] }
  const overlap = (before: Run, after: Run) =>
    centreOf(after.index) - markWidthOf(say(after.annotations)) / 2 <
    centreOf(before.index) + markWidthOf(say(before.annotations)) / 2 + markGap
  const runs: Run[] = []
  days.forEach(({ day }, index) => {
    const on = annotations.filter((annotation) => annotation.day === day)
    if (!on.length) return
    runs.push({ index, annotations: on })
    // A run grows wider as it takes the next mark in, so it may reach the one before it too.
    while (plotWidth !== undefined && runs.length > 1 && overlap(runs.at(-2)!, runs.at(-1)!))
      runs.at(-2)!.annotations.push(...runs.pop()!.annotations)
  })
  return runs.map(({ index, annotations }) => ({
    day: days[index]!.day,
    label: say(annotations),
    annotations,
  }))
}
